import React, { useState, useEffect } from 'react'
import {
  AptiveIcon,
  Dropdown,
  DropdownItem,
  Modal,
} from '@aptive-env/storybook'
import { format, getDay } from 'date-fns'
import classNames from 'classnames'
import CustomRecurrence from 'components/CustomRecurrence'
import { IEventData, ICustomEventTime, ICalendarEventInterval, IEventWeekDays } from 'interface/calendar'
import {
  eventConstants,
  EVENT_EVERY_WEEKDAY,
  EVENT_NOT_REPEATING,
  EVENT_REPEATED_CUSTOM,
  EVENT_MONTHLY,
  EVENT_WEEKLY,
} from 'constants/index'

const {
  eventRepeatTypes,
  monthWeeks,
  weekDates,
  weekDays,
  mapRepeatTypeToInterval,
} = eventConstants

interface IEventRepeatSelectProps {
  isOpen: boolean
  eventData: IEventData,
  setEventData: React.Dispatch<React.SetStateAction<IEventData>>
  disabled?: boolean
  event?: any
}

export const EventRepeatSelect: React.FC<IEventRepeatSelectProps> = ({
  isOpen,
  eventData,
  setEventData,
  disabled = false,
  event,
}) => {
  const [isRepeatDropdownOpen, setIsRepeatDropdownOpen] = useState(false)
  const [selectedRepeatType, setSelectedRepeatType] = useState(EVENT_NOT_REPEATING)
  const [isCustomRecurrenceSelected, setIsCustomRecurrenceSelected] = useState(false)
  const [isCustomDone, setIsCustomDone] = useState(false)
  const [customEventTime, setCustomEventTime] = useState<ICustomEventTime>({
    week_days: [''],
    end_after: 'never',
    occurrences: 1,
    repeat_every: 1,
    interval: 'daily',
    end_date: null,
    week_num: undefined,
  })

  useEffect(() => {
    if (selectedRepeatType !== EVENT_REPEATED_CUSTOM) {
      setEventData((prevEventData) => ({
        ...prevEventData,
        interval: mapRepeatTypeToInterval[selectedRepeatType as keyof typeof mapRepeatTypeToInterval] as ICalendarEventInterval,
      }))
      if (selectedRepeatType === EVENT_EVERY_WEEKDAY) {
        setEventData((prevEventData) => ({
          ...prevEventData,
          weekDays: weekDays as IEventWeekDays[],
        }))
      }
      if (selectedRepeatType === EVENT_WEEKLY) {
        setEventData((prevEventData) => ({
          ...prevEventData,
          weekDays: [weekDates[getDay(eventData.selectedDate)] as IEventWeekDays],
        }))
      }
    }
  }, [selectedRepeatType, setEventData, eventData.selectedDate])

  useEffect(() => {
    if (isCustomDone) {
      setEventData((prevEventData) => ({
        ...prevEventData,
        endAfter: customEventTime.end_after,
        interval: customEventTime.interval,
        repeatEvery: customEventTime.repeat_every,
        ...(customEventTime.end_date && {endDate: customEventTime.end_date}),
        ...(customEventTime.week_num && {weekNum: customEventTime.week_num}),
        ...(customEventTime.occurrences && {occurrences: customEventTime.occurrences}),
        ...(customEventTime.week_days.length && {weekDays: customEventTime.week_days as IEventWeekDays[]}),
      }))
    }
  }, [isCustomDone, setEventData, customEventTime])

  const toggleRepeatDropdown = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.stopPropagation()
    setIsRepeatDropdownOpen((prev) => !prev)
  }

  function handleRepeatDropdown(repeatType: string) {
    if (repeatType === EVENT_REPEATED_CUSTOM) {
      setIsCustomRecurrenceSelected(true)
    }
    setSelectedRepeatType(repeatType)
    setIsRepeatDropdownOpen(false)
    setIsCustomDone(false)
  }

  const handleCloseCustomRecurrence = () => {
    setIsCustomRecurrenceSelected(false)
    setSelectedRepeatType(EVENT_NOT_REPEATING)
  }

  const getCustomRepeatDetail = () => {
    const eventTimeParams = disabled ? {...event, week_days: event.week_days?.split(',')} : customEventTime
    let repeatDetail = ''

    if (eventTimeParams.repeat_every > 1) repeatDetail += `Every ${eventTimeParams.repeat_every} `

    switch (eventTimeParams.interval) {
    case 'once':
      repeatDetail += EVENT_NOT_REPEATING
      break
    case 'monthly-on-day':
      repeatDetail += EVENT_MONTHLY
      break
    case 'daily':
      repeatDetail += eventTimeParams.repeat_every > 1 ? 'days' : 'Daily'
      break
    case 'weekly':
      repeatDetail += eventTimeParams.repeat_every > 1 ? 'weeks on ' : 'Weekly on '
      eventTimeParams.week_days.forEach((week_day: string) => {
        repeatDetail += week_day.charAt(0).toUpperCase() + week_day.slice(1)
        repeatDetail +=
            week_day ===
            eventTimeParams.week_days[eventTimeParams.week_days.length - 1]
              ? ''
              : ', '
      })
      break
    case 'monthly':
      repeatDetail += eventTimeParams.repeat_every > 1 ? 'months on ' : 'Monthly on '
      if (eventTimeParams.week_num === undefined)
        repeatDetail += `day ${format(eventData.selectedDate, 'd')}`
      else if (eventTimeParams.week_num === -1)
        repeatDetail += `the last ${
          weekDates[getDay(eventData.selectedDate)].charAt(0).toUpperCase() +
            weekDates[getDay(eventData.selectedDate)].slice(1)
        }`
      else
        repeatDetail += `the ${
          monthWeeks[eventTimeParams.week_num] +
            ' ' +
            weekDates[getDay(eventData.selectedDate)].charAt(0).toUpperCase() +
            weekDates[getDay(eventData.selectedDate)].slice(1)
        }`
      break
    case 'yearly':
      repeatDetail += eventTimeParams.repeat_every > 1 ? 'years on ' : 'Annually on '
      repeatDetail += `${format(eventData.selectedDate, 'MMMM d')}`
      break
    }
    if (eventTimeParams.end_date) {
      const endDate = new Date(`${eventTimeParams.end_date}T00:00:00`)
      repeatDetail += ` until ${format(endDate, 'MMMM d, yyyy')}`
    }
    if (eventTimeParams.occurrences) repeatDetail += `, ${eventTimeParams.occurrences} times`

    return repeatDetail
  }

  return (
    <div>
      <p className="font-inter text-sm font-normal leading-4 text-dark-gray-100">
        When
      </p>
      <div className="relative z-[5] mt-1">
        <div
          className={classNames(
            'w-full h-[38px] border rounded-lg flex items-center justify-between px-3',
            disabled ? 'text-gray-400' : 'text-dark-gray-100 cursor-pointer'
          )}
          onClick={disabled ? () => {} : toggleRepeatDropdown}
        >
          <p
            className="font-inter text-sm font-normal leading-4"
          >
            {selectedRepeatType === EVENT_REPEATED_CUSTOM || disabled
              ? getCustomRepeatDetail()
              : selectedRepeatType}
          </p>
          <AptiveIcon
            icon="chevronDown"
            className="w-4 h-4 stroke-[#3D3D3D] fill-none cursor-pointer"
          />
        </div>
        <Dropdown
          className="!min-w-[262px] w-full"
          isOpen={isRepeatDropdownOpen}
          onClose={() => setIsRepeatDropdownOpen(false)}
        >
          {eventRepeatTypes.map((repeatType: string, index: number) => (
            <DropdownItem
              selected={selectedRepeatType === repeatType}
              onClick={() => handleRepeatDropdown(repeatType)}
              key={index}
            >
              {repeatType}
            </DropdownItem>
          ))}
        </Dropdown>
      </div>
      {isCustomRecurrenceSelected && (
        <Modal
          open={isOpen}
          className="w-[443px] min-h-[600px] flex flex-col"
          onClickOutside={handleCloseCustomRecurrence}
        >
          <CustomRecurrence
            onClose={() => setIsCustomRecurrenceSelected(false)}
            onCancel={handleCloseCustomRecurrence}
            data={customEventTime}
            setData={setCustomEventTime}
            pickedDate={eventData.selectedDate}
            setDone={setIsCustomDone}
          />
        </Modal>
      )}
    </div>
  )
}
